<script lang="ts">
  import { app, ds, resource } from '../lib/app.svelte';
  import { anchorRace } from '../lib/race';
  import { cancelScrollHold, holdScroll } from '../lib/anchor';
  import TopBar from './parts/TopBar.svelte';
  import Banners from './parts/Banners.svelte';
  import Scrubber from './parts/Scrubber.svelte';
  import Footer from './parts/Footer.svelte';
  import type { Component } from 'svelte';
  import Home from './pages/Home.svelte';

  // Home ships in the main bundle; other pages load on demand.
  type PageProps = { ov: import('../types').Overview | null; id?: string };
  const loaders: Record<string, () => Promise<{ default: Component<PageProps> }>> = {
    race: () => import('./pages/RacePage.svelte') as never,
    state: () => import('./pages/StatePage.svelte') as never,
    turnout: () => import('./pages/TurnoutPage.svelte') as never,
    diagnostics: () => import('./pages/DiagnosticsPage.svelte') as never,
    vote: () => import('./pages/VotePage.svelte') as never,
  };
  let Page = $state<Component<PageProps> | null>(null);
  let pageName = $state('');
  $effect(() => {
    const name = app.route.name;
    if (name === 'home') return;
    let alive = true;
    loaders[name]!().then((m) => { if (alive) { Page = m.default; pageName = name; } }, () => { if (alive) reloadOnce(); });
    return () => { alive = false; };
  });

  // After a deploy, an open tab asks for page chunks that no longer exist. Reload once to pick up the new build;
  // a timestamped sessionStorage flag stops a reload loop if the chunk is really gone.
  function reloadOnce(): boolean {
    const KEY = 'chsvotes-chunk-reload';
    let last = 0;
    try { last = Number(sessionStorage.getItem(KEY) ?? 0); } catch { /* storage blocked */ }
    if (Date.now() - last < 60_000) return false;
    try { sessionStorage.setItem(KEY, String(Date.now())); } catch { return false; }
    location.reload();
    return true;
  }
  $effect(() => {
    // only swallow the error when we are actually reloading; otherwise let the page's normal error path show it
    const onErr = (e: Event) => { if (reloadOnce()) e.preventDefault(); };
    window.addEventListener('vite:preloadError', onErr);
    return () => window.removeEventListener('vite:preloadError', onErr);
  });

  // One snapshot at a time: before the page moves to a new snapshot, load the files this view needs from it.
  // If any fail, the whole page stays on the previous snapshot (no mixing) and a notice says so; retried on the
  // next poll (live) or with backoff (replays).
  $effect(() => {
    const sid = app.viewSid, data = app.data, route = app.route.name, id = app.route.id;
    void app.poll;
    if (!sid || data.shownSid === sid) { if (sid) data.loadFailed = false; return; }
    let alive = true;
    const store = data.store;
    const need: Promise<unknown>[] = [store.overview(sid).then((o) => {
      const anchor = route === 'home' ? anchorRace(o.races.filter((r) => r.scope === 'charleston')) : null;
      return anchor ? store.race(sid, anchor.race_id) : null;
    })];
    if (route === 'race' && id) need.push(store.race(sid, id).catch((e) => (data.shownSid ? Promise.reject(e) : null)));
    if (route === 'turnout') need.push(store.turnout(sid));
    if (route === 'diagnostics') need.push(store.diagnostics(sid));
    Promise.all(need).then(
      () => { if (alive) { holdScroll(); data.shownSid = sid; data.loadFailed = false; data.noteOk(); } },
      () => {
        if (!alive) return;
        holdScroll();
        if (!data.shownSid) data.shownSid = sid; // nothing shown yet: let the page show its own error state
        else data.loadFailed = true;
        data.noteError();
      },
    );
    return () => { alive = false; };
  });

  const ov = resource(() => (app.shownSid ? ds().overview(app.shownSid) : null));
  $effect(() => {
    document.title = app.meta ? `SC Election Night · ${app.meta.election.name}` : 'SC Election Night';
  });
  // scroll to the top only when the page itself changes (not on snapshot changes, scrubbing or area picks)
  const pageKey = $derived(`${app.route.name}/${app.route.id ?? ''}`);
  $effect(() => {
    void pageKey;
    cancelScrollHold();
    window.scrollTo({ top: 0 });
  });
</script>

{#if app.route.name === 'vote'}
  <!-- full-screen page of its own (no results chrome) -->
  {#if Page && pageName === 'vote'}<Page ov={null} />{/if}
{:else}
  <TopBar ov={ov.value} />
  <Banners />
  <main class:loading={!ov.value}>
    {#if app.route.name === 'home'}
      <Home ov={ov.value} />
    {:else if Page && pageName === app.route.name}
      {#key app.route.id}<Page id={app.route.id ?? ''} ov={ov.value} />{/key}
    {:else}
      <div class="wrap"><div class="skel" style="height:320px;margin-top:28px"></div></div>
    {/if}
  </main>
  <Footer />
  <Scrubber />
{/if}

<style>
  /* keep the footer below the fold only while results load (avoids layout shift); once loaded the page ends
     right after the footer (+ the replay bar's height) */
  main.loading { min-height: 100vh; }
</style>
